import { useState } from 'react';
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { NotificationAudience } from '@wundo/shared/notificationHistory.ts';
import {
  sendNotification,
  sendNotificationBulk,
  type NotificationBulkSend,
  type NotificationRecipient,
} from '@/lib/notificationSend.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { resubscribeToPlatformEmail } from '@/lib/users.ts';
import { nameOf } from './sendLabels.ts';
import type { NotificationKindChoice } from './useNotificationKind.ts';
import type { SendOptions } from './useSendOptions.ts';

/** At most one dialog is open: the send confirm, or putting one person back on the mailing list. */
export type SendDialog =
  | { kind: 'closed' }
  | { kind: 'confirmSend' }
  | { kind: 'resubscribe'; person: NotificationRecipient };

export interface NotificationSend {
  dialog: SendDialog;
  sendError: string;
  /** What the last single send said, for a meeting moment. */
  lastSent: string;
  /** What the last press for a list queued, and who it left out. */
  bulkResult: NotificationBulkSend | null;
  resubscribe: UseMutationResult<void, Error, NotificationRecipient>;
  openConfirm: () => void;
  openResubscribe: (person: NotificationRecipient) => void;
  closeDialog: () => void;
  confirmSend: () => Promise<void>;
  confirmResubscribe: () => Promise<void> | undefined;
  clearLastSent: () => void;
  clearBulkResult: () => void;
}

interface SendInput {
  kind: NotificationKindChoice;
  options: SendOptions;
  recipient: NotificationRecipient | null;
  meetingId: string;
  selectedIds: string[];
  audience: NotificationAudience | undefined;
  /** Called once a list send is queued, so the ticks can be cleared. */
  onBulkSent: () => void;
}

/* A dialog shows a failure itself; its promise only says when to stop waiting. */
function ignoreOutcome(): undefined {
  return undefined;
}

export function useNotificationSend({
  kind,
  options,
  recipient,
  meetingId,
  selectedIds,
  audience,
  onBulkSent,
}: SendInput): NotificationSend {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<SendDialog>({ kind: 'closed' });
  const [sendError, setSendError] = useState('');
  const [lastSent, setLastSent] = useState('');
  const [bulkResult, setBulkResult] = useState<NotificationBulkSend | null>(null);

  const closeDialog = () => setDialog({ kind: 'closed' });
  const showSendError = (failure: Error) =>
    setSendError(apiErrorMessage(failure, 'Sending failed'));

  const invalidate = async (...keys: string[]) => {
    for (const key of keys) {
      await queryClient.invalidateQueries({ queryKey: [key] });
    }
  };

  const afterSend = async () => {
    setLastSent(`${kind.label} sent to ${nameOf(recipient!)}.`);
    closeDialog();
    await invalidate('notificationPreview');
  };

  /* The list is read again afterwards: its stamps are the report of what actually went. */
  const afterBulkSend = async (result: NotificationBulkSend) => {
    setBulkResult(result);
    closeDialog();
    onBulkSent();
    await invalidate('notificationRecipients', 'notificationRecipient');
  };

  const afterResubscribe = async () => {
    closeDialog();
    await invalidate(
      'notificationRecipients',
      'notificationRecipient',
      'notificationPreview',
    );
  };

  const send = useMutation({
    mutationFn: () =>
      sendNotification({
        kind: kind.kind,
        userId: recipient!.user_id,
        meetingId,
        channel: options.channel,
        resend: options.resend,
      }),
    onSuccess: afterSend,
    onError: showSendError,
  });

  const sendBulk = useMutation({
    mutationFn: () =>
      sendNotificationBulk({
        kind: kind.kind,
        userIds: [...selectedIds],
        neverName: options.neverName,
        roleplayId: options.roleplayId,
        channel: options.channel,
        audience,
        resend: options.resend,
      }),
    onSuccess: afterBulkSend,
    onError: showSendError,
  });

  /* The only way back onto the list, for somebody who asked us; see resubscribeToPlatformEmail. */
  const resubscribe = useMutation({
    mutationFn: (person: NotificationRecipient) =>
      resubscribeToPlatformEmail(person.user_id),
    onSuccess: afterResubscribe,
  });

  const openConfirm = () => {
    setSendError('');
    setDialog({ kind: 'confirmSend' });
  };

  const openResubscribe = (person: NotificationRecipient) => {
    resubscribe.reset();
    setDialog({ kind: 'resubscribe', person });
  };

  const confirmSend = () =>
    (kind.isUserScope ? sendBulk.mutateAsync() : send.mutateAsync()).then(
      ignoreOutcome,
      ignoreOutcome,
    );

  const confirmResubscribe = () =>
    dialog.kind === 'resubscribe'
      ? resubscribe.mutateAsync(dialog.person).then(ignoreOutcome, ignoreOutcome)
      : undefined;

  return {
    dialog,
    sendError,
    lastSent,
    bulkResult,
    resubscribe,
    openConfirm,
    openResubscribe,
    closeDialog,
    confirmSend,
    confirmResubscribe,
    clearLastSent: () => setLastSent(''),
    clearBulkResult: () => setBulkResult(null),
  };
}
